/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * `delay-seconds`, e.g. `retry-after: 30`.
 *
 * RFC 9110 defines it as `1*DIGIT` — a non-negative decimal integer — so this
 * matches digits only rather than reaching for `Number`, which would read `1e3`,
 * `0x10`, `12.5` or `+30` as second counts none of which the grammar allows. A value
 * that is not `1*DIGIT` is not delay-seconds, so it returns null and the caller tries
 * the HTTP-date form next.
 */
function parseDelaySeconds(raw: string): number | null {
    if (!/^\d+$/.test(raw)) {
        return null;
    }

    const seconds = Number(raw);

    return Number.isFinite(seconds) ? seconds * 1000 : null;
}

/**
 * HTTP-date, e.g. `retry-after: Wed, 21 Oct 2026 07:28:00 GMT`.
 *
 * RFC 9110 has recipients accept three formats, but senders MUST generate the
 * IMF-fixdate form above, which is the one the WAF sends and the one the engine's own
 * `Date` parser reads reliably. The obsolete rfc850 and asctime forms are left to
 * whatever `Date` makes of them rather than hand-parsed, since nothing is expected to
 * send them.
 */
function parseHttpDate(raw: string): number | null {
    const timestamp = new Date(raw).valueOf();

    return Number.isNaN(timestamp) ? null : timestamp - Date.now();
}

/**
 * Parses a `retry-after` response header into milliseconds.
 *
 * Handles both forms allowed by RFC 9110: `delay-seconds` and an HTTP-date. Returns
 * null when the header is missing or unusable, so callers can tell "wait zero ms"
 * apart from "the server didn't say" and fall back to a default wait.
 *
 * A negative wait is unusable rather than zero: a date already in the past describes
 * a window that has elapsed, so it tells us nothing about how long to wait and reads
 * the same as an absent header. A wait of exactly zero is kept — that is a server
 * saying "try again now".
 *
 * Parsing only; nothing here decides what a wait is worth acting on. A very short
 * wait, zero included, falls back to the one-minute default in `./countdown`, so "try
 * again now" still gives the visitor a notice they can read rather than a flash.
 */
export function parseRetryAfterMs(headerValue: unknown): number | null {
    if (typeof headerValue !== 'string' && typeof headerValue !== 'number') {
        return null;
    }

    const raw = String(headerValue).trim();
    if (!raw) {
        return null;
    }

    const waitMs = parseDelaySeconds(raw) ?? parseHttpDate(raw);

    if (waitMs === null || waitMs < 0) {
        return null;
    }

    return waitMs;
}
